import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box } from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { forwardRef, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import { ChevronLeft, ChevronRight } from '../../assets/icons';
import CarouselGlobalStyles from '../../theme/components/CarouselGlobalStyles';
import Icon from '../Icon/Icon.component';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function Carousel(_ref) {
  var children = _ref.children,
    activeSlide = _ref.activeSlide,
    setActiveSlide = _ref.setActiveSlide,
    additionalNavButtonStyles = _ref.additionalNavButtonStyles,
    _ref$focusOnLoad = _ref.focusOnLoad,
    focusOnLoad = _ref$focusOnLoad === void 0 ? true : _ref$focusOnLoad,
    _ref$infinite = _ref.infinite,
    infinite = _ref$infinite === void 0 ? true : _ref$infinite,
    _ref$arrows = _ref.arrows,
    arrows = _ref$arrows === void 0 ? true : _ref$arrows;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var slidesCount = children === null || children === void 0 ? void 0 : children.length;
  var prevRef = useRef(null);
  var nextRef = useRef(null);
  var sliderRef = useRef(null);
  useEffect(() => {
    if (focusOnLoad) {
      if (activeSlide >= slidesCount - 1) {
        var _prevRef$current;
        (_prevRef$current = prevRef.current) === null || _prevRef$current === void 0 || _prevRef$current.focus();
        focusSlider(sliderRef);
      }
      if (activeSlide <= 0) {
        var _nextRef$current;
        (_nextRef$current = nextRef.current) === null || _nextRef$current === void 0 || _nextRef$current.focus();
        focusSlider(sliderRef);
      }
    }
  }, [activeSlide, focusOnLoad, slidesCount]);
  useEffect(() => {
    focusOnLoad && focusSlider(sliderRef);
  }, [focusOnLoad]);
  useEffect(() => {
    if (activeSlide >= 0 && activeSlide < slidesCount) {
      var _sliderRef$current;
      (_sliderRef$current = sliderRef.current) === null || _sliderRef$current === void 0 || _sliderRef$current.slickGoTo(activeSlide);
    }
  }, [activeSlide, slidesCount]);
  return _jsxs(Box, {
    "data-testid": "carousel-".concat(activeSlide + 1, "/").concat(slidesCount),
    sx: {
      '& .slick-list div': {
        '&:focus, a': {
          outline: 'none'
        }
      }
    },
    children: [_jsx(CarouselGlobalStyles, {}), _jsx(Slider, {
      infinite: infinite,
      arrows: arrows,
      lazyLoad: "ondemand",
      initialSlide: activeSlide,
      speed: 300,
      afterChange: index => setActiveSlide(index),
      ref: sliderRef,
      nextArrow: activeSlide < slidesCount - 1 ? _jsx(ForwardedCustomNextArrow, _objectSpread({
        ref: nextRef,
        ariaLabel: t('common.carousel.next')
      }, additionalNavButtonStyles || {})) : _jsx(NoArrow, {}),
      prevArrow: activeSlide > 0 ? _jsx(ForwardedCustomPrevArrow, _objectSpread({
        ref: prevRef,
        ariaLabel: t('common.carousel.previous')
      }, additionalNavButtonStyles || {})) : _jsx(NoArrow, {}),
      children: children
    })]
  });
}
var navButtonStyles = {
  borderRadius: 'full',
  border: '2px solid var(--chakra-colors-baseWhite)',
  h: '2.813rem',
  w: '2.813rem',
  opacity: '75%',
  background: 'darkGrey2',
  zIndex: 1,
  _hover: {
    opacity: 1
  },
  position: 'absolute',
  top: '40%',
  marginRight: '0.7rem',
  marginLeft: '0.7rem'
};
function CustomNextArrow(_ref2, ref) {
  var ariaLabel = _ref2.ariaLabel,
    onClick = _ref2.onClick,
    boxStyles = _ref2.boxStyles,
    rightIconStyle = _ref2.rightIconStyle,
    rightArrowStyles = _ref2.rightArrowStyles;
  var handleClick = event => {
    event.preventDefault();
    event.stopPropagation();
    onClick === null || onClick === void 0 || onClick(event);
  };
  return _jsx(Box, _objectSpread(_objectSpread(_objectSpread(_objectSpread({
    ref: ref,
    as: "button",
    type: "button",
    onClick: handleClick,
    title: ariaLabel,
    "aria-label": ariaLabel,
    _focusVisible: {
      outline: '2px solid var(--chakra-colors-primary)',
      outlineOffset: '2px'
    },
    right: "xl"
  }, navButtonStyles), boxStyles || {}), rightArrowStyles || {}), {}, {
    children: _jsx(Icon, _objectSpread({
      svg: _jsx(ChevronRight, {
        color: "var(--chakra-colors-baseWhite)"
      }),
      transform: "translateX(1.6rem) scale(1.5)"
    }, rightIconStyle || {}))
  }));
}
var ForwardedCustomNextArrow = forwardRef(CustomNextArrow);
function CustomPrevArrow(_ref3, ref) {
  var ariaLabel = _ref3.ariaLabel,
    onClick = _ref3.onClick,
    boxStyles = _ref3.boxStyles,
    leftIconStyle = _ref3.leftIconStyle,
    leftArrowStyles = _ref3.leftArrowStyles;
  var handleClick = event => {
    event.preventDefault();
    event.stopPropagation();
    onClick === null || onClick === void 0 || onClick(event);
  };
  return _jsx(Box, _objectSpread(_objectSpread(_objectSpread(_objectSpread({
    ref: ref,
    as: "button",
    type: "button",
    onClick: handleClick,
    title: ariaLabel,
    "aria-label": ariaLabel,
    _focusVisible: {
      outline: '2px solid var(--chakra-colors-primary)',
      outlineOffset: '2px'
    },
    left: "xl"
  }, navButtonStyles), boxStyles || {}), leftArrowStyles || {}), {}, {
    children: _jsx(Icon, _objectSpread({
      svg: _jsx(ChevronLeft, {
        color: "var(--chakra-colors-baseWhite)"
      }),
      transform: "translateX(1.4rem) scale(1.5)"
    }, leftIconStyle || {}))
  }));
}
var ForwardedCustomPrevArrow = forwardRef(CustomPrevArrow);
function NoArrow() {
  return null;
}
function focusSlider(sliderRef) {
  var _sliderRef$current2;
  if (sliderRef !== null && sliderRef !== void 0 && (_sliderRef$current2 = sliderRef.current) !== null && _sliderRef$current2 !== void 0 && _sliderRef$current2.innerSlider) {
    var _sliderRef$current$in;
    var track = (_sliderRef$current$in = sliderRef.current.innerSlider.list) === null || _sliderRef$current$in === void 0 ? void 0 : _sliderRef$current$in.querySelector('.slick-track');
    var slide = track === null || track === void 0 ? void 0 : track.querySelector('.slick-slide');
    slide === null || slide === void 0 || slide.focus();
  }
}